import {Container, Matrix, Renderer, Sprite} from 'pixi.js';

const COLORS = {
    WHITE: 0xFFFFFF,
    GREY: 0x777777
} as const;

export type BoundsRect = { top: number; left: number; width: number; height: number; };

export function isClientRectsEqual(r1?: BoundsRect, r2?: BoundsRect): boolean {
    if (!r1 || !r2) {
        return false;
    }

    return (
        r1.left === r2.left
        && r1.top === r2.top
        && r1.width === r2.width
        && r1.height === r2.height
    );
}

export function isPixiMatricesEqual(m1?: Matrix, m2?: Matrix): boolean {
    if (!m1 || !m2) {
        return false;
    }

    return (
        m1.a === m2.a
        && m1.b === m2.b
        && m1.c === m2.c
        && m1.d === m2.d
        && m1.tx === m2.tx
        && m1.ty === m2.ty
    );
}

export function getWorldTransformCSSMatrix(renderer: Renderer, worldTransform: Matrix, resolution: number): string {
    return pixiMatrixToCSS(getDOMRelativeWorldTransform(renderer, worldTransform, resolution));
}

export function getDOMRelativeWorldTransform(renderer: Renderer, worldTransform: Matrix, resolution: number): Matrix {
    const canvasBounds = renderer.view.getBoundingClientRect();
    const matrix = worldTransform.clone();

    matrix.scale(resolution, resolution);
    matrix.scale(canvasBounds.width / renderer.width, canvasBounds.height / renderer.height);

    return matrix;
}

export function pixiMatrixToCSS(m: Matrix): string {
    return `matrix(${[m.a, m.b, m.c, m.d, m.tx, m.ty].join(',')})`;
}

export function getCanvasBounds(renderer: Renderer): BoundsRect {
    const {top, left, width, height} = renderer.view.getBoundingClientRect();
    const bounds = {top, left, width, height};

    bounds.left += window.scrollX;
    bounds.top += window.scrollY;

    return bounds;
}

export function traverse(item: Container, cb: (item: Container) => void): void {
    cb(item);

    item.children
        ?.forEach(item => traverse(item as Container, cb));
}

export function find(item: Container, cb: (item: Container) => boolean|void|undefined): Container | undefined {
    if (cb(item)) {
        return item;
    }

    return item
        .children
        ?.find(item => find(item as Container, cb)) as Container | undefined;
}

export function findMultiple(component: Container, cb: (item: Container) => boolean|void|undefined): Container[] {
    const result: Container[] = [];

    traverse(component, item => {
        if (cb(item)) {
            result.push(item);
        }
    });

    return result;
}

export function findCollection<K extends string>(component: Container, names: K[]): {[key in K]?: Container} {
    const result = {} as {[key in K]?: Container};

    traverse(component, item => {
        if (names.includes(item.name as K)) {
            (result as any)[item.name] = item;
        }
    });

    return result;
}

export function tintAllSpritesTree(item: Container, color: number = COLORS.GREY): void {
    traverse(item, item => {
        if (item instanceof Sprite) {
            item.tint = color;
        }
    });
}

export function tintAll(item: Container, color: number = COLORS.GREY): void {
    traverse(item, item => {
        if ('tint' in item) {
            (item as any)['tint'] = color;
        }
    });
}

export function removeTintAll(item: Container): void {
    tintAll(item, COLORS.WHITE)
}

export function removeTintAllSpritesTree(item: Container): void {
    tintAllSpritesTree(item, COLORS.WHITE);
}
